import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { 
  ShoppingCart, Plus, Trash2, RefreshCw, CheckCircle, 
  Apple, Beef, Milk, Wheat, Package, ArrowLeft
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  proteins: Beef,
  dairy: Milk,
  produce: Apple,
  grains: Wheat,
  pantry: Package,
  frozen: Package,
  other: ShoppingCart
};

const CATEGORY_COLORS = {
  proteins: 'text-red-400',
  dairy: 'text-blue-400',
  produce: 'text-green-400',
  grains: 'text-amber-400',
  pantry: 'text-purple-400',
  frozen: 'text-cyan-400',
  other: 'text-gray-400'
};

const GroceryListPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [groceryList, setGroceryList] = useState(null);
  const [newItem, setNewItem] = useState('');
  const [addingItem, setAddingItem] = useState(false);

  useEffect(() => {
    fetchGroceryList();
  }, []);

  const fetchGroceryList = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.get(`${API}/api/family/grocery-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.has_list) {
        setGroceryList(response.data.grocery_list);
      }
    } catch (error) {
      console.error('Failed to fetch grocery list:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleItem = async (category, itemName) => {
    try {
      const token = localStorage.getItem('aura_token');
      
      // Find current state
      const items = groceryList.items[category] || [];
      const item = items.find(i => i.name === itemName);
      const newChecked = !item?.checked;
      
      // Optimistic update
      setGroceryList(prev => ({
        ...prev,
        items: {
          ...prev.items,
          [category]: prev.items[category].map(i => 
            i.name === itemName ? { ...i, checked: newChecked } : i
          )
        }
      }));
      
      await axios.post(`${API}/api/family/grocery-list/toggle-item`, 
        { item: itemName, category, checked: newChecked },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      toast.error('Failed to update item');
      fetchGroceryList();
    }
  };

  const handleAddItem = async () => {
    if (!newItem.trim()) return;
    
    setAddingItem(true);
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/api/family/grocery-list/add-item`,
        { item: newItem.trim(), category: 'other' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Item added');
      setNewItem('');
      fetchGroceryList();
    } catch (error) {
      toast.error('Failed to add item');
    } finally {
      setAddingItem(false);
    }
  };

  const handleClearChecked = async () => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.post(`${API}/api/family/grocery-list/clear-checked`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Checked items removed');
      fetchGroceryList();
    } catch (error) {
      toast.error('Failed to clear items');
    }
  };

  const getCheckedCount = () => {
    if (!groceryList?.items) return 0;
    return Object.values(groceryList.items)
      .flat()
      .filter(item => item.checked).length;
  };

  const getTotalCount = () => {
    if (!groceryList?.items) return 0;
    return Object.values(groceryList.items).flat().length;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-aura-primary animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  if (!groceryList) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Button variant="ghost" onClick={() => navigate('/goals')} className="text-aura-text-muted">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Goals
          </Button>
          
          <Card className="bg-aura-surface border-white/10">
            <CardContent className="p-8 text-center">
              <ShoppingCart className="w-16 h-16 text-aura-text-muted mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white">No Grocery List Yet</h2>
              <p className="text-aura-text-muted mt-2">
                Generate a meal plan first to auto-create your grocery list
              </p>
              <Button 
                onClick={() => navigate('/goals')} 
                className="mt-6 bg-aura-primary hover:bg-aura-primary-hover"
              >
                Go to Goals & Meal Plan
              </Button>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-24">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button variant="ghost" onClick={() => navigate('/goals')} className="text-aura-text-muted mb-2">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Goals
            </Button>
            <h1 className="text-2xl font-heading font-bold text-white uppercase tracking-tight">
              Grocery List
            </h1>
            <p className="text-aura-text-muted mt-1">
              {getCheckedCount()} of {getTotalCount()} items checked
            </p>
          </div>
          
          {getCheckedCount() > 0 && (
            <Button 
              variant="outline" 
              className="border-red-500/30 text-red-400"
              onClick={handleClearChecked}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Checked
            </Button>
          )}
        </div>

        {/* Add Item */}
        <Card className="bg-aura-surface border-white/10">
          <CardContent className="p-4">
            <div className="flex gap-3">
              <Input
                placeholder="Add item to list..."
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddItem()}
                className="bg-aura-bg border-white/10 text-white"
              />
              <Button 
                onClick={handleAddItem}
                disabled={addingItem}
                className="bg-aura-primary hover:bg-aura-primary-hover"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Categories */}
        {Object.entries(groceryList.items || {}).map(([category, items]) => {
          if (!items || items.length === 0) return null;
          
          const CategoryIcon = CATEGORY_ICONS[category] || ShoppingCart;
          const colorClass = CATEGORY_COLORS[category] || 'text-gray-400';
          const checkedInCategory = items.filter(i => i.checked).length;
          
          return (
            <Card key={category} className="bg-aura-surface border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="text-white font-heading uppercase tracking-wider flex items-center gap-2 text-sm">
                  <CategoryIcon className={`w-4 h-4 ${colorClass}`} />
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                  <Badge variant="secondary" className="ml-auto bg-white/10 text-white">
                    {checkedInCategory}/{items.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {items.map((item, idx) => (
                    <div 
                      key={`${item.name}-${idx}`}
                      className={`flex items-center gap-3 p-2 rounded-lg transition-all ${
                        item.checked ? 'bg-green-500/10' : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <Checkbox
                        checked={item.checked}
                        onCheckedChange={() => handleToggleItem(category, item.name)}
                        className="border-white/30 data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500"
                      />
                      <span className={`flex-1 ${item.checked ? 'line-through text-aura-text-muted' : 'text-white'}`}>
                        {item.name}
                      </span>
                      {item.count > 1 && (
                        <Badge variant="secondary" className="bg-white/10 text-aura-text-muted">
                          x{item.count}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}

        {/* Info */}
        {groceryList.from_meal_plan && (
          <p className="text-xs text-aura-text-muted text-center">
            Auto-generated from your meal plan • Share with family to coordinate shopping
          </p>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GroceryListPage;
